import { createStore, createTypedHooks } from 'easy-peasy'
import type { FormStepResult } from '../types'
import type { FMPStoreModel } from '../types'
import { formModel } from './models/formModel'
import { billModel } from './models/billModel'
import { consumptionPredictionModel } from './models/consumptionPredictionModel'
import { enrichCustomerModel } from './models/enrichCustomer'
import appModel from './models/appModel'
import loggerModel from './models/loggerModel'
import { envModel } from './models/envModel'
import { siteConfigModel } from './models/siteConfigModel'
import phoneConfigModel from './models/phoneConfigModel'
import type { DomainEnum } from '@strata-monorepo/shared-types/src/Domain'
import metricsModel from './models/metricsModel'

/**
 * Creates the find-my-plan store with optional initial state
 * @param initialFormState Optional initial form state
 * @param siteDomain Optional site domain for configuration
 * @returns The configured easy-peasy store
 */
export const createFMPStore = (
  initialFormState: FormStepResult = {},
  siteDomain: DomainEnum | null = null,
  formId?: string,
) => {
  const model: FMPStoreModel = {
    app: appModel(),
    env: envModel(),
    form: formModel(initialFormState, formId),
    bill: billModel(),
    enrichCustomer: enrichCustomerModel(),
    consumptionPrediction: consumptionPredictionModel(),
    logger: loggerModel(),
    metrics: metricsModel(),
    siteConfig: siteConfigModel(siteDomain),
    phoneConfig: phoneConfigModel(),
    // Add more models as the library grows
  }

  // Create store without persistence to avoid formId conflicts
  // Form data will not persist across page reloads
  return createStore(model)
}

// Create typed hooks for the store
const typedHooks = createTypedHooks<FMPStoreModel>()

/**
 * Default store instance with empty initial state
 */
export const store = createFMPStore({}, null)

export type TypedHooks = typeof typedHooks
export type TypedStoreState = TypedHooks['useStoreState']
export type TypedStoreActions = TypedHooks['useStoreActions']

// Export the typed hooks
// Use these instead of the hooks from the easy-peasy package in components
export const useStoreState: TypedStoreState = typedHooks.useStoreState
export const useStoreActions: TypedStoreActions = typedHooks.useStoreActions
